// Spanish (Latin America, tú) copy for /web-uploader. Same shape as copy.en.ts (checked by copy.test.ts).
// "La subida web" is the site's Spanish name for the web uploader (pricing and preview-first pages).
// The uploader's own labels are in English, so they're quoted as it shows them («Previews», «Originals»).
import type { WebUploaderCopy } from "./copy.en";

const es: WebUploaderCopy = {
  seo: {
    title: "Subida web de Photohawk | Más rápida que el FTP",
    description:
      "Renovamos la subida web de Photohawk. Envía muchas fotos a la vez, maneja subidas de más de 100\u00a0000 fotos y se reanuda sola si se corta la conexión, en todos los planes. En todos los planes, sube primero vistas previas ligeras para que tu galería esté en línea en minutos, no en horas.",
  },
  hero: {
    badge: "Nuevo",
    eyebrow: "En todos los planes",
    title: "La nueva subida web de Photohawk",
    subtitle:
      "Más rápida que el FTP, pensada para subidas de más de 100\u00a0000 fotos y retoma donde se quedó.",
    plans: "En todos los planes, con las vistas previas primero un evento grande está en línea en minutos, no en horas.",
    ctaPrimary: "Abrir la subida web",
    ctaSecondary: "Ver las novedades",
    screenshotAlt:
      "La subida web a mitad de una subida. Con las vistas previas primero (todos los planes), la barra de estado indica que la galería está en línea con las vistas previas y muestra cuántas personas la visitan, y la cola de la derecha muestra las vistas previas terminadas y los originales en camino, con dos fotos compradas que van primero.",
    phoneAlt: "La subida web en un celular, abierta en una carpeta de la galería, con el estado de la subida abajo.",
  },
  previews: {
    heading: "Primero las vistas previas",
    badge: "Todos los planes",
    subtitle:
      "En todos los planes: en línea en minutos, no en horas. Una galería de 10\u00a0000 fotos puede estar en línea y vendiendo en unos 8 minutos con una velocidad de subida de 20 Mbps.",
    body: "Primero se suben vistas previas ligeras, para que tu galería pueda estar en línea y empezar a vender, o a dar descargas gratuitas, mientras llegan los originales. Si alguien compra o descarga una foto antes de que llegue su original, ese original se adelanta en la cola.",
    steps: [
      { id: "previews", text: "Primero suben las vistas previas ligeras" },
      { id: "live", text: "Tu galería está en línea" },
      { id: "buy", text: "Alguien compra una foto" },
      { id: "jump", text: "Su original se adelanta en la cola" },
      { id: "originals", text: "Llegan los originales" },
    ],
    anim: {
      label: "Ilustración animada de las vistas previas primero",
      description:
        "Las vistas previas ligeras de seis fotos vuelan de tu computadora a tu galería, y la galería pasa a estar en línea. Alguien toca Comprar en una foto: su original pasa al frente de la cola de originales y sube primero. Después llegan los demás originales.",
      computer: "Tu computadora",
      gallery: "Tu galería",
      notLive: "Aún no en línea",
      live: "En línea",
      buy: "Comprar",
      bought: "Comprada",
      queue: "Originales",
      goesFirst: "Va primero",
    },
    link: "Cómo funcionan las subidas con vista previa primero",
  },
  faster: {
    heading: "Por qué es más rápida que el FTP",
    subtitle: "Las mismas fotos, enviadas de dos maneras.",
    anim: {
      label: "Comparación animada de una app de FTP y la subida web",
      description:
        "Dos filas. Arriba, una app de FTP envía 2 fotos a la vez por defecto, y como máximo 10. Abajo, la subida web envía muchas fotos a la vez por carriles que se ensanchan y se estrechan según la conexión. En todos los planes, primero van las vistas previas ligeras, así su galería está en línea antes. La conexión se corta un momento y la subida se reanuda sola.",
      ftp: "App de FTP",
      ftpNote: "2 a la vez por defecto",
      ftpMax: "como máximo 10",
      web: "Subida web",
      webNote: "Muchas a la vez",
      adapts: "Se adapta a tu ancho de banda",
      gallery: "Galería",
      live: "En línea",
      dropped: "Se cortó la conexión",
      resumed: "Se reanudó sola",
      legendPreviews: "Vistas previas",
      legendOriginals: "Originales",
    },
    points: [
      {
        id: "parallel",
        title: "Muchas fotos a la vez",
        body: "Envía muchas fotos a la vez, muchas más que una app de FTP como FileZilla (2 a la vez por defecto, y como máximo 10).",
      },
      {
        id: "adaptive",
        title: "Aprovecha al máximo tu conexión",
        body: "Se va ajustando mientras sube, para aprovechar al máximo tu conexión.",
      },
      {
        id: "scale",
        title: "Pensada para eventos grandes",
        body: "La probamos a gran escala y maneja sin problemas subidas de más de 100\u00a0000 fotos.",
      },
      {
        id: "live",
        title: "En línea en minutos, no en horas (todos los planes)",
        body: "En todos los planes, primero suben vistas previas ligeras, así una galería de 10\u00a0000 fotos puede estar en línea y vendiendo en unos 8 minutos con una velocidad de subida de 20 Mbps, mientras llegan los originales.",
      },
      {
        id: "resume",
        title: "Retoma donde se quedó",
        body: "Si se corta la conexión, se reanuda sola. Agrega la carpeta otra vez y solo se envía lo que falta.",
      },
    ],
  },
  estimate: {
    heading: "¿Cuánto tardará tu subida?",
    subtitle: "Una estimación para tu galería, según tu propia velocidad de subida.",
    heroLink: "Calcula tu tiempo de subida",
    photos: "Número de fotos",
    custom: "Otro",
    customLabel: "Número de fotos (otro)",
    size: "Tamaño medio de un original",
    sizeUnit: "MB",
    sizeHint: "Los JPEG de eventos suelen rondar los {mb} MB.",
    speed: "Tu velocidad de subida",
    speedUnit: "Mbps",
    test: "Probar mi velocidad de subida",
    testing: "Probando… {mbps} Mbps",
    testStarting: "Probando…",
    testDone: "Medido: {mbps} Mbps de subida",
    testFailed: "No pudimos medir tu velocidad ahora. Escríbela tú, o prueba un test de velocidad general.",
    dataNote: "Usa hasta unos {mb} MB de datos, y solo cuando tocas el botón.",
    otherTest: "O usa un test de velocidad general",
    resultsHeading: "Tu estimación",
    previewsLabel: "Vistas previas primero",
    previewsPlans: "Todos los planes",
    previewsLive: "Galería en línea en {time}",
    previewsAll: "Todos los originales subidos en {time}",
    originalsLabel: "Solo originales",
    originalsLive: "Galería en línea en {time}",
    originalsWhen: "cuando estén arriba los {gb} GB de originales",
    computerNote: "A esta velocidad, lo que marca el ritmo es lo rápido que tu computadora crea las vistas previas.",
    disclaimer: "Solo son estimaciones. Los tiempos reales dependen de tu conexión, tu computadora y tus fotos.",
    time: {
      under: "menos de un minuto",
      min: "unos {n} min",
      h: "unas {n} h",
      d: "unos {n} días",
    },
    table: {
      caption: "Ejemplo: 10\u00a0000 fotos",
      speed: "Velocidad de subida",
      live: "Vistas previas primero: galería en línea (todos los planes)",
      all: "Todos los originales subidos",
      note: "La cifra de unos 8 minutos se midió con una velocidad de subida de 20 Mbps (vistas previas primero). Las demás filas son estimaciones del mismo modelo, con originales de unos {mb} MB.",
    },
  },
  features: {
    heading: "Más novedades",
    subtitle: "Pantallas reales de la nueva subida web.",
    items: [
      {
        id: "status",
        title: "El estado, en palabras simples",
        body: "Ve de un vistazo cómo va todo. Con las vistas previas primero (todos los planes): «Previews» (vistas previas) y después «Originals» (originales), cada uno con su propio progreso.",
        alt: "El estado de la subida con las vistas previas primero (todos los planes): «Live — saving originals…», una fila de progreso para Previews y otra para Originals, y «2 photos people bought go first».",
      },
      {
        id: "queue",
        title: "Una cola que va donde tú quieras",
        body: "Coloca la cola de subida a la derecha, a la izquierda o abajo, y arrástrala al tamaño que prefieras. Tu navegador recuerda tu elección.",
        alt: "La cola de subida acoplada abajo, con su menú abierto para moverla a la derecha, abajo o a la izquierda.",
      },
      {
        id: "visitors",
        title: "Mira quién está viendo",
        body: "La barra de estado muestra cuántas personas están viendo tu galería en este momento, en todos los planes.",
        alt: "La barra de estado arriba de la subida web: «Live on previews» (con las vistas previas primero) y el número de visitantes.",
      },
      {
        id: "resume",
        title: "Retoma donde se quedó",
        body: "Si se corta la conexión, se reanuda sola. Agrega la carpeta otra vez y solo se envía lo que falta.",
        alt: "Un aviso en la subida web que dice que una carpeta no terminó de subirse, con un botón Resume.",
      },
      {
        id: "phone",
        title: "Clara también en el celular",
        body: "En una pantalla pequeña, la ruta de carpetas se pliega para caber y, al tocar «…», ves las carpetas intermedias. Así siempre sabes adónde van tus fotos.",
        alt: "La subida web en un celular: la ruta muestra la carpeta actual y «…» abre la lista de las carpetas superiores.",
      },
    ],
  },
  ftp: {
    date: "1 de noviembre de 2026",
    heading: "Qué cambia con el FTP el 1 de noviembre",
    body: "Nuestros servidores FTP compartidos siempre activos cierran el 1 de noviembre de 2026. ¿Usas FTP hoy? Cámbiate antes de esa fecha.",
    points: [
      {
        id: "closes",
        title: "El FTP compartido cierra el 1 de noviembre de 2026",
        body: "Los servidores FTP compartidos siempre activos se apagan el 1 de noviembre de 2026.",
      },
      {
        id: "standard",
        title: "La subida web es la forma estándar de subir fotos",
        body: "Está en todos los planes y puedes usarla desde hoy.",
      },
      {
        id: "ondemand",
        title: "Si el FTP es imprescindible",
        body: "En todos los planes, cada persona de tu espacio de trabajo puede iniciar su propio servidor FTP bajo demanda desde la subida web, con una dirección personal que nunca cambia. Se apaga tras una hora sin actividad.",
      },
    ],
    helpLink: "Cómo funciona el FTP bajo demanda",
  },
  more: {
    previewFirstTitle: "Cómo funcionan las subidas con vista previa primero",
    previewFirstBody: "En todos los planes. Paso a paso: las vistas previas, la publicación y lo que ven los compradores.",
  },
  cta: {
    heading: "Pruébala en tu próxima subida",
    body: "La nueva subida web está lista en todos los planes. Agrega una carpeta y mira cómo tu galería se pone en línea.",
    button: "Abrir la subida web",
  },
  pause: "Pausar la animación",
  play: "Reproducir la animación",
};

export default es;
